import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environment/environment';
import { LoggerService } from '@app/core/infrastructure';
import {
  UserPreferencesSchema,
  UserPreferencesUpdateInput,
  OnboardingStateUpdateInput,
  UiPreferencesUpdateInput
} from './user-preferences-api.models';

/**
 * Pure API service for user preferences HTTP operations
 * No state management - just HTTP calls
 */
@Injectable({
  providedIn: 'root'
})
export class UserPreferencesApiService {
  private readonly logger = inject(LoggerService).scope('UserPreferencesApiService');

  private apiBaseUrl = `${environment.API_BASE_URL}/protected/users`;

  constructor(private http: HttpClient) {
    this.logger.debug('Initialized');
  }

  /**
   * GET /me/preferences - Retrieve user preferences
   * Auto-creates record with defaults if not exists
   */
  public getPreferences(): Observable<UserPreferencesSchema> {
    return this.http.get<UserPreferencesSchema>(`${this.apiBaseUrl}/me/preferences`).pipe(
      catchError(error => {
        this.logger.error('Error fetching user preferences:', error);
        return throwError(() => new Error('Failed to fetch user preferences'));
      })
    );
  }

  /**
   * PUT /me/preferences - Update user preferences
   * Replaces provided fields only (omitted fields unchanged)
   */
  public updatePreferences(data: UserPreferencesUpdateInput): Observable<UserPreferencesSchema> {
    return this.http.put<UserPreferencesSchema>(`${this.apiBaseUrl}/me/preferences`, data).pipe(
      catchError(error => {
        this.logger.error('Error updating user preferences:', error);
        return throwError(() => new Error('Failed to update user preferences'));
      })
    );
  }

  /**
   * PATCH /me/preferences/onboarding-state - Merge onboarding state
   * Merges data instead of replacing
   */
  public updateOnboardingState(data: OnboardingStateUpdateInput): Observable<UserPreferencesSchema> {
    return this.http.patch<UserPreferencesSchema>(
      `${this.apiBaseUrl}/me/preferences/onboarding-state`,
      data
    ).pipe(
      catchError(error => {
        this.logger.error('Error updating onboarding state:', error);
        return throwError(() => new Error('Failed to update onboarding state'));
      })
    );
  }

  /**
   * PATCH /me/preferences/ui-preferences - Merge UI preferences
   * Merges data instead of replacing
   */
  public updateUiPreferences(data: UiPreferencesUpdateInput): Observable<UserPreferencesSchema> {
    return this.http.patch<UserPreferencesSchema>(
      `${this.apiBaseUrl}/me/preferences/ui-preferences`,
      data
    ).pipe(
      catchError(error => {
        this.logger.error('Error updating UI preferences:', error);
        return throwError(() => new Error('Failed to update UI preferences'));
      })
    );
  }
}
